import { useState, useEffect } from 'react';
import { Box, Button, Container, TextField, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { login as apiLogin, logout as apiLogout, fetchMe, getStoredUser } from './api/auth';
import { getToken } from './api/client';
import Dashboard from './pages/Dashboard';
import Sidebar from './components/Sidebar';
import SystemSelect from './pages/SystemSelect';
import Assets from './pages/Assets';
import DeviceDetail from './pages/DeviceDetail';
import AssetGroup from './pages/AssetGroup';
import Vulnerabilities from './pages/Vulnerabilities';
import CredentialedScan from './pages/CredentialedScan';
import StigCompliance from './pages/StigCompliance';
import Alerts from './pages/Alerts';
import ComplyToConnect from './pages/ComplyToConnect';
import AtoRmf from './pages/AtoRmf';
import AtoExport from './pages/AtoExport';
import Odp from './pages/Odp';
import DmzTopology from './pages/DmzTopology';
import AiAssistant from './pages/AiAssistant';
import Architecture from './pages/Architecture';
import UserManagement from './pages/UserManagement';
import Layout from './components/Layout';
import logo from './assets/logo.jpeg';

function AccessDenied() {
  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
      <LockIcon sx={{ fontSize: 48, color: '#1e3a5f' }} />
      <Typography sx={{ color: '#7a8fa6', fontSize: 18, fontWeight: 700 }}>Access Denied</Typography>
      <Typography sx={{ color: '#4a5a70', fontSize: 13 }}>You do not have permission to view this page.</Typography>
    </Box>
  );
}
function App() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggedIn, setLoggedIn] = useState(() => !!getToken());
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [permissions, setPermissions] = useState(() => getStoredUser()?.permissions || []);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceContext, setDeviceContext] = useState(null);

  const navigateToDevice = (device, context = null) => {
    setSelectedDevice(device);
    setDeviceContext(context);
    setActivePage('device');
  };

  const [selectedGroup, setSelectedGroup] = useState(null);
  const navigateToGroup = (group) => {
    setSelectedGroup(group);
    setActivePage('assetGroup');
  };

  const [localOverrides, setLocalOverrides] = useState({});
  const [customRanges, setCustomRanges] = useState([]);

  // Refresh permissions from server on app load
  useEffect(() => {
    if (loggedIn) {
      fetchMe()
        .then(me => {
          setCurrentUser({ username: me.username, role: me.role, permissions: me.permissions });
          setPermissions(me.permissions);
        })
        .catch(() => {
          apiLogout();
          setLoggedIn(false);
          setCurrentUser(null);
          setPermissions([]);
        });
    }
  }, [loggedIn]);

  const handleLogin = async () => {
    if (!username || !password) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      const data = await apiLogin(username, password);
      setCurrentUser({ username: data.username, role: data.role, permissions: data.permissions });
      setPermissions(data.permissions || []);
      setLoggedIn(true);
    } catch (err) {
      setLoginError('Invalid username or password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    apiLogout();
    setLoggedIn(false);
    setCurrentUser(null);
    setPermissions([]);
    setSelectedSystem(null);
  };

  const can = (page) => permissions.includes(page);

  if (loggedIn && !selectedSystem) return <SystemSelect onSelect={setSelectedSystem} />;

  if (loggedIn) return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar activePage={activePage} setActivePage={setActivePage} permissions={permissions} />
      <Box sx={{ marginLeft: '64px', flex: 1 }}>
        <Layout system={selectedSystem} onSystemChange={() => setSelectedSystem(null)} activePage={activePage} onNavigateDashboard={() => setActivePage('dashboard')} onNavigate={setActivePage} currentUser={currentUser} onLogout={handleLogout}>
          {activePage === 'dashboard'    && (can('dashboard')   ? <Dashboard setActivePage={setActivePage} onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} /> : <AccessDenied />)}
          {activePage === 'assets'       && (can('assets')      ? <Assets onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} localOverrides={localOverrides} setLocalOverrides={setLocalOverrides} customRanges={customRanges} setCustomRanges={setCustomRanges} /> : <AccessDenied />)}
          {activePage === 'device'       && <DeviceDetail device={selectedDevice} context={deviceContext} onBack={() => setActivePage('assets')} onBackToGroup={() => setActivePage('assetGroup')} />}
          {activePage === 'assetGroup'   && <AssetGroup group={selectedGroup} onBack={() => setActivePage('assets')} onSelectDevice={(device) => navigateToDevice(device, { fromGroup: selectedGroup })} />}
          {activePage === 'vulns'        && (can('vulns')       ? <Vulnerabilities onNavigate={setActivePage} />                                              : <AccessDenied />)}
          {activePage === 'cred_scan'   && <CredentialedScan onBack={() => setActivePage('vulns')} />}
          {activePage === 'stig'         && (can('stig')        ? <StigCompliance onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} />              : <AccessDenied />)}
          {activePage === 'alerts'       && (can('alerts')      ? <Alerts />                                                                                         : <AccessDenied />)}
          {activePage === 'c2c'          && (can('c2c')         ? <ComplyToConnect />                                                                                : <AccessDenied />)}
          {activePage === 'ato'          && (can('ato')         ? <AtoRmf />                                                                                         : <AccessDenied />)}
          {activePage === 'odp'          && (can('odp')         ? <Odp currentUser={currentUser} />                                                                  : <AccessDenied />)}
          {activePage === 'export'       && (can('export')      ? <AtoExport />                                                                                      : <AccessDenied />)}
          {activePage === 'dmz'          && (can('dmz')         ? <DmzTopology onSelectDevice={navigateToDevice} onSelectGroup={navigateToGroup} />                  : <AccessDenied />)}
          {activePage === 'arch'         && (can('arch')        ? <Architecture />                                                                                   : <AccessDenied />)}
          {activePage === 'ai'           && (can('ai')          ? <AiAssistant />                                                                                    : <AccessDenied />)}
          {activePage === 'user_management' && (can('user_management') ? <UserManagement /> : <AccessDenied />)}
        </Layout>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0a0e1a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            backgroundColor: '#111827',
            border: '1px solid #1e3a5f',
            borderRadius: 2,
          }}
        >
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
            <img src={logo} alt="Eighth Technologies" style={{ width: 64, height: 64, borderRadius: '50%', marginBottom: 8 }} />
            <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, letterSpacing: 2 }}>
              ASPYN
            </Typography>
            <Typography variant="body2" sx={{ color: '#7a8fa6', marginTop: 0.5 }}>
              Aggregated Security Platform for Your Network
            </Typography>
          </Box>

          {/* Login Form */}
          <TextField
            fullWidth
            label="Username"
            variant="outlined"
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            InputLabelProps={{ style: { color: '#7a8fa6' } }}
            InputProps={{ style: { color: '#ffffff' } }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e3a5f' } } }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            InputLabelProps={{ style: { color: '#7a8fa6' } }}
            InputProps={{ style: { color: '#ffffff' } }}
            sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#1e3a5f' } } }}
          />

          {loginError && (
            <Alert severity="error" sx={{ mt: 1, backgroundColor: '#2a0a0a', color: '#f44336', '& .MuiAlert-icon': { color: '#f44336' } }}>
              {loginError}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={loginLoading}
            sx={{
              marginTop: 2,
              backgroundColor: '#00bcd4',
              color: '#0a0e1a',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#0097a7' },
              '&:disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
            }}
          >
            {loginLoading ? <CircularProgress size={22} sx={{ color: '#4a5a70' }} /> : 'Sign In'}
          </Button>

          <Typography variant="caption" sx={{ color: '#7a8fa6', display: 'block', textAlign: 'center', marginTop: 2 }}>
            Eighth Technologies, LLC — Authorized Users Only
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default App;
